import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "../../common/hooks/useSelector";
import {
  selectEffectiveLayerView,
  selectHoveredMaskUrl,
  selectRightPanelKind,
  selectSpecsEditorNav,
} from "../../common/slices/specsEditor/selectors";
import { specsEditorActions } from "../../common/slices/specsEditor/slice";
import {
  clearSession,
  clearUploadState,
  loadUploadState,
  saveGlobalApiKey,
  saveUploadState,
  loadGlobalApiKey,
} from "./storage";
import {
  SpecsLayer,
  SpecsEditorNav,
  SpecsEditorSession,
  SpecsEditorIdentity,
  SpecsInput,
  ImageSide,
  SpecsEditorIconPickerTarget,
  SpecsLayerView,
} from "./types";
import type { SpecsEditorModalsApi } from "./SpecsEditorModals";
import { resolveInitialSpecsModel } from "./SpecsModelContext";
import { useSpecsEditorGlobalOutputs } from "./useSpecsEditorGlobalOutputs";
import { useSpecsEditorLayers } from "./useSpecsEditorLayers";
import { useSpecsEditorElements } from "./useSpecsEditorElements";
import { useSpecsEditorParts } from "./useSpecsEditorParts";
import { useSpecsEditorProductImport } from "./useSpecsEditorProductImport";
import { useSpecsEditorStyles } from "./useSpecsEditorStyles";
import { getElementSelectionKey, getStylePositionKey } from "./positionsLib";
import type { SpecsRevisionHistoryValue } from "./useSpecsRevisionHistory";
import type { CompositePreviewState } from "./useCompositePreview";
import type { SpecsCatalog } from "./specsModel/catalog";
import { SpecsModel } from "./specsModel/SpecsModel";
import type { ElementModel } from "./specsModel/ElementModel";
import type { VersionModel } from "./specsModel/VersionModel";
import type { PartModel } from "./specsModel/PartModel";
import { LayerModel } from "./specsModel/LayerModel";
import { makeLayerRuntimeKey } from "./specsEditorRuntimeKeys";

type SelectedStyleScope = {
  partName: string;
  elementName: string;
  styleName: string;
};

type SelectedElementNavScope = {
  partName: string;
  elementName: string;
  styleName?: string;
};

type SpecsModelVersion = NonNullable<ReturnType<SpecsModel["getVersion"]>>;
type DesignElementCandidate = NonNullable<
  ReturnType<SpecsModelVersion["getElement"]>
>;
type SelectedElementBinding = {
  part: PartModel;
  element: ElementModel;
};

function getModelLayerZIndex(layer: LayerModel, fallback: number) {
  return layer.sides.find((side) => side.zIndex !== null)?.zIndex ?? fallback;
}

function getSelectedStyleScope(nav: SpecsEditorNav): SelectedStyleScope | null {
  const scope = getSelectedElementScope(nav);
  if (!scope?.styleName) {
    return null;
  }

  return {
    partName: scope.partName,
    elementName: scope.elementName,
    styleName: scope.styleName,
  };
}

function getSelectedElementScope(
  nav: SpecsEditorNav
): SelectedElementNavScope | null {
  const isScopedNav =
    nav.kind === "element" || nav.kind === "layer" || nav.kind === "positions";
  if (!isScopedNav) {
    return null;
  }

  if (!nav.part || !nav.element) {
    return null;
  }

  return {
    partName: nav.part,
    elementName: nav.element,
    styleName: nav.style,
  };
}

function getDesignElementCandidates(
  version: SpecsModelVersion,
  selectedScope: SelectedElementNavScope | null
): DesignElementCandidate[] {
  if (selectedScope) {
    const selected = version.getElement(
      selectedScope.elementName,
      selectedScope.partName
    );
    return selected ? [selected] : [];
  }

  return version.parts.flatMap((part) =>
    part.elements.map((element) => ({ part, element }))
  );
}

function getDesignStyleName(
  element: ElementModel,
  selectedScope: SelectedElementNavScope | null
) {
  return (
    selectedScope?.styleName ?? element.defaultStyle ?? element.styles[0]?.name
  );
}

export function createModelDesignNav(
  version: SpecsModelVersion,
  nav: SpecsEditorNav,
  identity: SpecsEditorIdentity
): SpecsEditorNav | null {
  const selectedScope = getSelectedElementScope(nav);

  for (const { part, element } of getDesignElementCandidates(
    version,
    selectedScope
  )) {
    const styleName = getDesignStyleName(element, selectedScope);
    if (!styleName) {
      continue;
    }

    const style = element.styles.find(
      (candidate) => candidate.name === styleName
    );
    if (!style) {
      continue;
    }

    const layer =
      style.layers.find((candidate) => candidate.displayable) ??
      style.layers[0];
    if (layer) {
      const layerId = makeLayerRuntimeKey({
        partName: part.name,
        elementName: element.name,
        styleName,
        layerName: layer.name,
      });
      return {
        kind: "layer",
        product: identity.productName,
        version: identity.productVersion,
        part: part.name,
        element: element.name,
        style: styleName,
        layerId,
      };
    }

    return {
      kind: "element",
      product: identity.productName,
      version: identity.productVersion,
      part: part.name,
      element: element.name,
      style: styleName,
    };
  }

  return null;
}

export function getActiveStyleVisibleLayers(
  layers: SpecsLayer[],
  version: VersionModel | null,
  selectedStyleScope?: SelectedStyleScope | null
) {
  return layers.filter((layer) => {
    if (!layer.visible) {
      return false;
    }

    const path = layer.modelLayerPath;
    if (!path) {
      return true;
    }

    if (
      selectedStyleScope?.partName === path.partName &&
      selectedStyleScope.elementName === path.elementName
    ) {
      return selectedStyleScope.styleName === path.styleName;
    }

    if (!version) {
      return false;
    }

    const modelElement = version.getElement(path.elementName, path.partName);
    if (!modelElement) {
      return false;
    }

    const activeStyleName =
      modelElement.element.defaultStyle ||
      modelElement.element.styles[0]?.name ||
      "";

    return activeStyleName === path.styleName;
  });
}

export type SpecsEditorContextValue = {
  editor: SpecsEditorApi;
  revisionHistory: SpecsRevisionHistoryValue;
  modals: SpecsEditorModalsApi;
  session: SpecsEditorSession;
  currentVersion: ReturnType<SpecsModel["getVersion"]> | null;
  getInheritanceGroups: (partName?: string) => string[];
  layerDisplayGroups: string[];
  nav: SpecsEditorNav;
  importSpecs: SpecsCatalog | null;
  importLoading: boolean;
  importSelectedProduct: string;
  importSelectedVersion: string;
  selectedLayer: SpecsLayer | null;
  effectiveLayerView: SpecsLayerView;
  hoveredMaskUrl: string | null | undefined;
  editableOutputImages: any[];
  previewComposite: CompositePreviewState;
  globalPreviewComposite: CompositePreviewState;
  selectedPanelKind: string;
  selectedElement: SelectedElementBinding | null;
  selectedElementModel: ElementModel | null;
  selectedStyleName: string | null;
  selectedPositionId: string | null;
  assignedStyleNamesForSelectedElement: string[] | null;
  assignedElementKeysForSelectedPosition: Set<string> | null;
  allLayers: SpecsLayer[];
  visibleLayers: SpecsLayer[];
  setImportSelectedProduct: (productName: string) => void;
  setImportSelectedVersion: (versionName: string) => void;
  openImportDialog: () => void | Promise<void>;
  selectSettings: () => void;
  selectHistory: () => void;
  selectPositions: (positionId?: string) => void;
  selectParts: (partName?: string) => void;
  selectElements: () => void;
  selectStyles: () => void;
  selectLayerSpecs: () => void;
  selectShipping: () => void;
  handleSelectDesign: () => void;
  selectMetadata: () => void;
  selectGlobal: () => void;
  selectFamilies: (specName?: string) => void;
  selectFabrics: (specName?: string) => void;
  selectDecorations: (specName?: string) => void;
  selectImport: () => void;
  selectFamiliesHistory: (specName?: string) => void;
  selectFabricsHistory: (specName?: string) => void;
  selectDecorationsHistory: (specName?: string) => void;
  selectLayer: (layerId: string) => void;
  setHoveredLayer: (layerId: string | null) => void;
  removeLayer: (layerId: string) => void;
  updateLayerColor: (layerId: string, color: string) => void;
  updateLayerZIndex: (layerId: string, zIndex: number) => void;
  addLayer: () => void;
  addPart: (partName: string) => void;
  renamePart: (partName: string, nextPartName: string) => void;
  removePart: (partName: string) => void;
  reorderPart: (partName: string, targetIndex: number) => void;
  moveElementToPart: (
    fromPartName: string,
    elementName: string,
    toPartName: string
  ) => void;
  updatePart: (
    partName: string,
    patch: Partial<Pick<PartModel, "mainElement" | "required" | "inheritFrom">>
  ) => void;
  updateElement: (
    elementName: string,
    patch: Record<string, any>,
    partName?: string
  ) => void;
  removeElement: (elementName: string, partName?: string) => void;
  addElement: (elementName: string, partName?: string) => void;
  addStyle: (elementName: string, styleName: string, partName?: string) => void;
  removeStyle: (
    elementName: string,
    styleName: string,
    partName?: string
  ) => void;
  updateStyle: (
    elementName: string,
    styleName: string,
    nextStyleName: string,
    partName?: string
  ) => void;
  updateStyleIcon: (
    elementName: string,
    styleName: string,
    nextUrl: string,
    partName?: string
  ) => void;
  setActiveStyle: (
    partName: string,
    elementName: string,
    styleName: string
  ) => void;
};

export const SpecsEditorContext = createContext<SpecsEditorContextValue | null>(
  null
);

type UseSpecsEditorArgs = {
  sessionKey?: string;
  initialImportProduct?: string;
  initialImportVersion?: string;
};

export function useSpecsEditor({
  sessionKey,
  initialImportProduct = "",
  initialImportVersion = "",
}: UseSpecsEditorArgs = {}) {
  const dispatch = useDispatch();
  const runtime = useSelector(
    (state) => state.specsEditor
  ) as SpecsEditorSession;
  const session = runtime;
  const selectedNav = useSelector(selectSpecsEditorNav);

  // Existing products (sessionKey provided via URL) are loaded fresh from
  // specs every time. Scratch sessions (no sessionKey, i.e. /specs with no
  // product) also start clean so opening the specs editor for a new product does not
  // unexpectedly revive a previous draft.
  const isExistingProduct = !!sessionKey;
  const storageKey = "scratch";
  const uploadStorageKey = sessionKey || storageKey;

  // Start with a clean in-memory session for scratch mode and remove any legacy
  // full-session persistence.
  const hydratedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (isExistingProduct) return;
    if (hydratedKeyRef.current === storageKey) return;
    hydratedKeyRef.current = storageKey;
    dispatch(specsEditorActions.resetRuntimeState());
    void clearSession(storageKey);
    void loadGlobalApiKey().then((key) => {
      if (key) dispatch(specsEditorActions.updateFalSettings({ apiKey: key }));
    });
  }, [dispatch, storageKey, isExistingProduct]);

  // Always load global API key (shared across all modes)
  useEffect(() => {
    if (!isExistingProduct) return; // already handled above for scratch
    void loadGlobalApiKey().then((key) => {
      if (key) dispatch(specsEditorActions.updateFalSettings({ apiKey: key }));
    });
  }, [dispatch, isExistingProduct]);

  const [specsModel, setSpecsModel] = useState<SpecsModel | null>(null);
  const [specsModelRevision, setSpecsModelRevision] = useState(0);
  const productImport = useSpecsEditorProductImport({
    initialImportProduct,
    initialImportVersion,
    setSpecsModel,
  });
  const [identityOverrides, setIdentityOverrides] = useState<
    Partial<SpecsEditorIdentity>
  >({});
  const selectedPanelKind = useSelector(selectRightPanelKind);
  const identity = useMemo(() => {
    const productName =
      identityOverrides.productName ||
      productImport.importSelectedProduct ||
      specsModel?.product.name ||
      "";
    const productDisplayName = specsModel?.product.getDisplayName() ?? "";
    const productVersion =
      identityOverrides.productVersion ||
      productImport.importSelectedVersion ||
      "";
    return {
      productName,
      productDisplayName,
      productVersion,
    };
  }, [
    identityOverrides.productName,
    identityOverrides.productVersion,
    productImport.importSelectedProduct,
    productImport.importSelectedVersion,
    specsModel,
    specsModelRevision,
  ]);
  const selectedPositionId =
    selectedNav.kind === "positions" ? (selectedNav.positionId ?? null) : null;
  useEffect(() => {
    if (!specsModel) {
      setSpecsModelRevision(0);
      return;
    }

    setSpecsModelRevision(specsModel.getRevision());
    return specsModel.subscribe(() => {
      setSpecsModelRevision(specsModel.getRevision());
    });
  }, [specsModel]);
  const stylePositionIdsByKey = useMemo(
    () =>
      Object.fromEntries(
        (
          specsModel
            ?.getVersion(identity.productVersion)
            ?.getPositionStyleAssignments() ?? []
        ).map((assignment) => [assignment.styleKey, assignment.positionIds])
      ) as Record<string, string[]>,
    [identity.productVersion, specsModel, specsModelRevision]
  );
  const currentVersion = useMemo(
    () => specsModel?.getVersion(identity.productVersion) ?? null,
    [identity.productVersion, specsModel, specsModelRevision]
  );
  const rawVersionMetadata = useMemo(
    () => currentVersion?.getRawVersionMetadata() ?? null,
    [currentVersion, specsModelRevision]
  );
  const editorLayers = useMemo(() => {
    if (!currentVersion) {
      return [];
    }

    const modelLayerKeys = new Set<string>();
    const nameCounts = new Map<string, number>();
    const layers: SpecsLayer[] = [];
    let modelLayerIndex = 0;

    for (const part of currentVersion.parts) {
      for (const element of part.elements) {
        for (const style of element.styles) {
          for (const layer of style.layers) {
            modelLayerIndex += 1;
            const path = {
              partName: part.name,
              elementName: element.name,
              styleName: style.name,
              layerName: layer.name,
            };
            const key = makeLayerRuntimeKey(path);
            modelLayerKeys.add(key);

            const layerName = layer.layerLabel || layer.name;
            const nextNameCount = (nameCounts.get(layerName) ?? 0) + 1;
            nameCounts.set(layerName, nextNameCount);
            const displayName =
              nextNameCount > 1 ? `${layerName} (${nextNameCount})` : layerName;
            const runtimeLayer = runtime.layerRuntimeByKey[key];
            const fallbackZIndex = modelLayerIndex * 100;

            layers.push({
              id: key,
              name: displayName,
              visible: runtimeLayer?.visible ?? layer.displayable,
              baseColor:
                runtimeLayer?.baseColor ??
                (layer.canInherit(element)
                  ? LayerModel.INHERITED_COLOR_NAME
                  : layer.getDefaultPreviewColor(
                      productImport.importSpecs?.fabrics ?? []
                    )),
              zIndex:
                runtimeLayer?.zIndex ??
                getModelLayerZIndex(layer, fallbackZIndex),
              modelLayerPath: path,
              highlight: runtimeLayer?.highlight ?? null,
              mask: runtimeLayer?.mask ?? null,
            });
          }
        }
      }
    }

    for (const key of runtime.layerRuntimeOrder) {
      if (modelLayerKeys.has(key)) {
        continue;
      }

      const runtimeLayer = runtime.layerRuntimeByKey[key];
      if (!runtimeLayer) {
        continue;
      }

      layers.push({
        id: runtimeLayer.key,
        name:
          runtimeLayer.modelLayerPath.layerName ??
          runtimeLayer.modelLayerPath.partName,
        visible: runtimeLayer.visible,
        baseColor: runtimeLayer.baseColor,
        zIndex: runtimeLayer.zIndex,
        modelLayerPath: runtimeLayer.modelLayerPath,
        highlight: runtimeLayer.highlight,
        mask: runtimeLayer.mask,
      });
    }

    layers.sort((left, right) => {
      const leftIndex = runtime.layerRuntimeOrder.indexOf(left.id);
      const rightIndex = runtime.layerRuntimeOrder.indexOf(right.id);
      if (leftIndex === -1 && rightIndex === -1) {
        return left.zIndex - right.zIndex;
      }
      if (leftIndex === -1) return 1;
      if (rightIndex === -1) return -1;
      return leftIndex - rightIndex;
    });

    return layers;
  }, [currentVersion, productImport.importSpecs, runtime, specsModelRevision]);
  const getInheritanceGroups = useCallback(
    (partName?: string) => currentVersion?.getInheritanceGroups(partName) ?? [],
    [currentVersion, specsModelRevision]
  );
  const selectedLayer = useMemo(() => {
    if (selectedNav.kind === "layer") {
      return (
        editorLayers.find((layer) => layer.id === selectedNav.layerId) ?? null
      );
    }
    if (
      selectedNav.kind === "element" ||
      selectedNav.kind === "positions" ||
      selectedNav.kind === "layerSpecs"
    ) {
      const scoped = editorLayers.filter(
        (layer) =>
          layer.modelLayerPath?.partName === selectedNav.part &&
          layer.modelLayerPath?.elementName === selectedNav.element &&
          layer.modelLayerPath?.styleName === selectedNav.style
      );
      if (selectedNav.kind === "layerSpecs" && selectedNav.layerName) {
        return (
          scoped.find(
            (layer) => layer.modelLayerPath?.layerName === selectedNav.layerName
          ) ?? null
        );
      }
      return scoped.find((layer) => layer.visible) ?? scoped[0] ?? null;
    }
    return null;
  }, [editorLayers, selectedNav]);
  const selectedStyleName =
    selectedNav.kind === "element" ||
    selectedNav.kind === "layer" ||
    selectedNav.kind === "positions" ||
    selectedNav.kind === "layerSpecs"
      ? (selectedNav.style ?? null)
      : null;
  const visibleLayers = useMemo(() => {
    if (
      selectedNav.kind === "element" ||
      selectedNav.kind === "layer" ||
      selectedNav.kind === "layerSpecs" ||
      (selectedNav.kind === "positions" &&
        selectedNav.part &&
        selectedNav.element &&
        selectedNav.style)
    ) {
      return editorLayers.filter(
        (layer) =>
          layer.visible &&
          layer.modelLayerPath?.partName === selectedNav.part &&
          layer.modelLayerPath?.elementName === selectedNav.element &&
          layer.modelLayerPath?.styleName === selectedNav.style
      );
    }
    return editorLayers.filter((layer) => layer.visible);
  }, [editorLayers, selectedNav]);
  const allVisibleLayers = useMemo(
    () =>
      getActiveStyleVisibleLayers(
        editorLayers,
        currentVersion,
        getSelectedStyleScope(selectedNav)
      ),
    [currentVersion, editorLayers, selectedNav]
  );
  const layerActions = useSpecsEditorLayers({
    currentVersion,
    identity,
    selectedLayer,
    selectedNav,
    sessionLayers: editorLayers,
  });
  const layerDisplayGroups = useMemo(
    () =>
      Array.from(
        new Set(
          currentVersion?.parts.flatMap((part) =>
            part.elements.flatMap((element) =>
              element.styles.flatMap((style) =>
                style.layers.map((layer) => layer.displayGroup).filter(Boolean)
              )
            )
          ) ?? []
        )
      ),
    [currentVersion, specsModelRevision]
  );
  const selectedElement = useMemo(() => {
    if (
      selectedNav.kind !== "element" &&
      selectedNav.kind !== "layer" &&
      selectedNav.kind !== "positions"
    ) {
      return null;
    }

    if (!selectedNav.part || !selectedNav.element) {
      return null;
    }

    if (!currentVersion) {
      return null;
    }

    return currentVersion.getElement(selectedNav.element, selectedNav.part);
  }, [currentVersion, selectedNav]);
  const selectedElementModel = useMemo(() => {
    return selectedElement?.element ?? null;
  }, [selectedElement]);
  const selectedElementKey = selectedElement
    ? getElementSelectionKey(
        selectedElement.part.name,
        selectedElement.element.name
      )
    : null;
  const assignedStyleNamesForSelectedElement = useMemo(() => {
    if (!selectedPositionId || !selectedElement) return null;

    const partName = selectedElement.part.name;
    const elementName = selectedElement.element.name;

    return selectedElement.element.styles
      .filter((style) => {
        const styleKey = getStylePositionKey(partName, elementName, style.name);
        const assignedPositionIds = stylePositionIdsByKey[styleKey] ?? [];
        return assignedPositionIds.includes(selectedPositionId);
      })
      .map((style) => style.name);
  }, [selectedElement, selectedPositionId, stylePositionIdsByKey]);
  const assignedElementKeysForSelectedPosition = useMemo(() => {
    if (!selectedPositionId) return null;
    if (!currentVersion) return null;

    const elementKeys = new Set<string>();
    for (const part of currentVersion.parts) {
      for (const element of part.elements) {
        const hasAssignedStyle = element.styles.some((style) => {
          const styleKey = getStylePositionKey(
            part.name,
            element.name,
            style.name
          );
          const assignedPositionIds = stylePositionIdsByKey[styleKey] ?? [];
          return assignedPositionIds.includes(selectedPositionId);
        });

        if (hasAssignedStyle) {
          elementKeys.add(getElementSelectionKey(part.name, element.name));
        }
      }
    }

    return elementKeys;
  }, [currentVersion, selectedPositionId, stylePositionIdsByKey]);
  const globalOutputActions = useSpecsEditorGlobalOutputs({
    currentVersion,
    identity: {
      productName: identity.productName,
      productVersion: identity.productVersion,
    },
    session,
    specsModel,
    specsModelRevision,
    fabrics: productImport.importSpecs?.fabrics ?? [],
    visibleLayers,
    allVisibleLayers,
  });
  const firstAvailableElementForSelectedPosition = useMemo(() => {
    if (!selectedPositionId) return null;
    if (!currentVersion) return null;
    if (!assignedElementKeysForSelectedPosition?.size) return null;

    for (const part of currentVersion.parts) {
      for (const element of part.elements) {
        const elementKey = getElementSelectionKey(part.name, element.name);
        if (assignedElementKeysForSelectedPosition.has(elementKey)) {
          return { part, element };
        }
      }
    }

    return null;
  }, [
    assignedElementKeysForSelectedPosition,
    currentVersion,
    selectedPositionId,
  ]);

  useEffect(() => {
    if (selectedNav.kind !== "positions") return;
    if (!selectedPositionId) return;
    if (!assignedElementKeysForSelectedPosition?.size) return;
    if (
      selectedElement &&
      selectedElementKey &&
      assignedElementKeysForSelectedPosition.has(selectedElementKey)
    ) {
      return;
    }

    const targetElement = firstAvailableElementForSelectedPosition;
    if (!targetElement) return;

    const targetStyleName =
      targetElement.element.styles.find((style) => {
        const styleKey = getStylePositionKey(
          targetElement.part.name,
          targetElement.element.name,
          style.name
        );
        const assignedPositionIds = stylePositionIdsByKey[styleKey] ?? [];
        return assignedPositionIds.includes(selectedPositionId);
      })?.name ??
      targetElement.element.defaultStyle ??
      targetElement.element.styles[0]?.name;

    if (!targetStyleName) return;

    dispatch(
      specsEditorActions.selectElement({
        partName: targetElement.part.name,
        elementName: targetElement.element.name,
        styleName: targetStyleName,
      })
    );
  }, [
    assignedElementKeysForSelectedPosition,
    dispatch,
    firstAvailableElementForSelectedPosition,
    selectedElement,
    selectedElementKey,
    selectedNav.kind,
    selectedPositionId,
    stylePositionIdsByKey,
  ]);

  useEffect(() => {
    if (selectedNav.kind !== "positions") return;
    if (!selectedElement || !selectedPositionId || !selectedStyleName) return;
    if (!assignedStyleNamesForSelectedElement?.length) return;
    if (assignedStyleNamesForSelectedElement.includes(selectedStyleName)) {
      return;
    }

    dispatch(
      specsEditorActions.setActiveStyle({
        partName: selectedElement.part.name,
        elementName: selectedElement.element.name,
        styleName: assignedStyleNamesForSelectedElement[0],
      })
    );
  }, [
    assignedStyleNamesForSelectedElement,
    dispatch,
    selectedElement,
    selectedNav.kind,
    selectedPositionId,
    selectedStyleName,
  ]);

  const getFirstAssignedStyleNameForElement = useCallback(
    (part: PartModel, element: ElementModel) =>
      element.styles.find((style) => {
        const styleKey = getStylePositionKey(
          part.name,
          element.name,
          style.name
        );
        const assignedPositionIds = stylePositionIdsByKey[styleKey] ?? [];
        return selectedPositionId
          ? assignedPositionIds.includes(selectedPositionId)
          : false;
      })?.name ??
      element.defaultStyle ??
      element.styles[0]?.name,
    [selectedPositionId, stylePositionIdsByKey]
  );

  useEffect(() => {
    const resolvedModel = resolveInitialSpecsModel({
      importSpecs: (productImport.importSpecs as SpecsCatalog | null) ?? null,
      importSelectedProduct: productImport.importSelectedProduct,
      importSelectedVersion: productImport.importSelectedVersion,
      currentProductName: identity.productName,
      currentVersionName: identity.productVersion,
    });

    if (resolvedModel) {
      setSpecsModel(resolvedModel);
      return;
    }

    if (
      isExistingProduct ||
      productImport.importSelectedProduct ||
      productImport.importSelectedVersion
    ) {
      setSpecsModel(null);
    }
  }, [
    isExistingProduct,
    identity.productName,
    identity.productVersion,
    productImport.importSelectedProduct,
    productImport.importSelectedVersion,
    productImport.importSpecs,
    session.id,
  ]);

  // Hydrate uploaded images only. Keep these in IndexedDB because re-uploading
  // large PSD/image sources is expensive during iteration.
  const hydratedUploadsRef = useRef<string | null>(null);
  useEffect(() => {
    if (hydratedUploadsRef.current === uploadStorageKey) return;
    hydratedUploadsRef.current = uploadStorageKey;

    void loadUploadState<{
      input: SpecsInput | null;
      backInput: SpecsInput | null;
    }>(uploadStorageKey).then((stored) => {
      if (stored?.input) {
        dispatch(specsEditorActions.setInput(stored.input));
      }
      if (stored?.backInput !== undefined) {
        dispatch(specsEditorActions.setBackInput(stored.backInput));
      }
    });
  }, [dispatch, uploadStorageKey]);

  // Persist only uploaded images, not the wider product/spec editor session.
  useEffect(() => {
    const existingDebugState = window.specs;
    const debugState = {
      ...existingDebugState,
      model: specsModel ?? undefined,
      get snapshot(): ReturnType<SpecsModel["save"]> | undefined {
        return this.model?.save();
      },
    };

    window.specs = debugState;
    void saveUploadState(uploadStorageKey, {
      input: session.input,
      backInput: session.backInput,
    });
  }, [specsModel, session.input, session.backInput, uploadStorageKey]);

  // Cleanup window ref on unmount
  useEffect(() => {
    return () => {
      delete window.specs;
    };
  }, []);

  const updateIdentity = useCallback(
    (patch: Partial<SpecsEditorIdentity>) => {
      const nextPatch = { ...patch };
      delete nextPatch.productDisplayName;
      setIdentityOverrides((current) => ({
        ...current,
        ...nextPatch,
      }));

      if (specsModel) {
        if (nextPatch.productName !== undefined) {
          specsModel.setIdentity({ productName: nextPatch.productName });
        }
        if (patch.productDisplayName !== undefined) {
          specsModel.setIdentity({
            productDisplayName: patch.productDisplayName,
          });
        }
        if (
          nextPatch.productVersion !== undefined &&
          nextPatch.productVersion !== identity.productVersion
        ) {
          specsModel.renameVersion(
            identity.productVersion,
            nextPatch.productVersion
          );
        }
      }
    },
    [identity.productVersion, specsModel]
  );
  const selectDesign = useCallback(() => {
    const modelNav = currentVersion
      ? createModelDesignNav(currentVersion, selectedNav, identity)
      : null;
    if (modelNav) {
      dispatch(specsEditorActions.setNav(modelNav));
      return;
    }

    dispatch(specsEditorActions.selectDesign());
  }, [currentVersion, dispatch, identity, selectedNav]);
  const selectLayerById = useCallback(
    (layerId: string) => {
      const layer = editorLayers.find((item) => item.id === layerId);
      const path = layer?.modelLayerPath;
      if (path) {
        if (selectedNav.kind === "positions") {
          dispatch(
            specsEditorActions.setNav({
              ...selectedNav,
              part: path.partName,
              element: path.elementName,
              style: path.styleName,
              layerId,
            })
          );
          return;
        }

        dispatch(
          specsEditorActions.setNav({
            kind: "layer",
            product: identity.productName,
            version: identity.productVersion,
            part: path.partName,
            element: path.elementName,
            style: path.styleName,
            layerId,
          })
        );
        return;
      }

      dispatch(specsEditorActions.selectLayer(layerId));
    },
    [
      dispatch,
      editorLayers,
      identity.productName,
      identity.productVersion,
      selectedNav,
    ]
  );
  const defaultDesignLoadKeyRef = useRef("");
  useEffect(() => {
    if (
      selectedNav.kind !== "empty" ||
      !currentVersion ||
      !identity.productName ||
      !identity.productVersion
    ) {
      return;
    }

    const loadKey = `${identity.productName}:${identity.productVersion}`;
    if (defaultDesignLoadKeyRef.current === loadKey) {
      return;
    }

    defaultDesignLoadKeyRef.current = loadKey;
    selectDesign();
  }, [
    currentVersion,
    identity.productName,
    identity.productVersion,
    selectDesign,
    selectedNav.kind,
  ]);
  const styleActions = useSpecsEditorStyles({
    currentVersion,
    layers: editorLayers,
    selectedNav,
    session,
  });
  const partActions = useSpecsEditorParts({
    currentVersion,
    layers: editorLayers,
    selectedNav,
    session,
  });
  const elementActions = useSpecsEditorElements({
    currentVersion,
    layers: editorLayers,
    selectedNav,
    session,
  });

  return {
    session,
    identity,
    selectedNav,
    selectedPanelKind,
    selectedLayer,
    selectedStyleName,
    selectedElement,
    selectedElementModel,
    allLayers: editorLayers,
    visibleLayers,
    specsModel,
    currentVersion,
    setSpecsModel,
    getInheritanceGroups,
    layerDisplayGroups,
    ...productImport,
    // Input
    setInput: (input: SpecsInput) =>
      dispatch(specsEditorActions.setInput(input)),
    setBackInput: (input: SpecsInput | null) =>
      dispatch(specsEditorActions.setBackInput(input)),
    ...layerActions,
    ...globalOutputActions,
    // Settings
    updateFalSettings: (patch: Partial<SpecsEditorSession["falSettings"]>) => {
      dispatch(specsEditorActions.updateFalSettings(patch));
      if (patch.apiKey !== undefined) {
        void saveGlobalApiKey(patch.apiKey);
      }
    },
    updateIdentity,
    assignedStyleNamesForSelectedElement,
    assignedElementKeysForSelectedPosition,
    getFirstAssignedStyleNameForElement,
    rawVersionMetadata,
    updateRawVersionMetadata: (metadata: Record<string, any> | null) => {
      currentVersion?.setRawVersionMetadata(metadata);
    },
    ...partActions,
    ...elementActions,
    ...styleActions,
    // UI state
    setNav: (nav: SpecsEditorNav) => dispatch(specsEditorActions.setNav(nav)),
    selectSettings: () => dispatch(specsEditorActions.selectSettings()),
    selectHistory: () => dispatch(specsEditorActions.selectHistory()),
    selectPositions: (
      payload?:
        | string
        | {
            positionId?: string;
            partName?: string;
            elementName?: string;
            styleName?: string;
            layerId?: string;
          }
    ) => dispatch(specsEditorActions.selectPositions(payload)),
    selectParts: (partName?: string) =>
      dispatch(specsEditorActions.selectParts(partName)),
    selectElements: () => dispatch(specsEditorActions.selectElements()),
    selectStyles: () => dispatch(specsEditorActions.selectStyles()),
    selectLayerSpecs: () => dispatch(specsEditorActions.selectLayerSpecs()),
    selectShipping: () => dispatch(specsEditorActions.selectShipping()),
    selectMetadata: () => dispatch(specsEditorActions.selectMetadata()),
    selectGlobal: () => dispatch(specsEditorActions.selectGlobal()),
    selectFamilies: (specName?: string) =>
      dispatch(specsEditorActions.selectFamilies(specName)),
    selectFabrics: (specName?: string) =>
      dispatch(specsEditorActions.selectFabrics(specName)),
    selectDecorations: (specName?: string) =>
      dispatch(specsEditorActions.selectDecorations(specName)),
    selectImport: () => dispatch(specsEditorActions.selectImport()),
    selectFamiliesHistory: (specName?: string) =>
      dispatch(specsEditorActions.selectFamiliesHistory(specName)),
    selectFabricsHistory: (specName?: string) =>
      dispatch(specsEditorActions.selectFabricsHistory(specName)),
    selectDecorationsHistory: (specName?: string) =>
      dispatch(specsEditorActions.selectDecorationsHistory(specName)),
    selectDesign,
    selectElement: (
      partName: string,
      elementName: string,
      styleName?: string
    ) =>
      dispatch(
        specsEditorActions.selectElement({
          partName,
          elementName,
          styleName,
        })
      ),
    selectLayer: selectLayerById,
    setLayerView: (view: SpecsLayerView) =>
      dispatch(specsEditorActions.setLayerView(view)),
    setHoveredLayer: (id: string | null) =>
      dispatch(specsEditorActions.setHoveredLayer(id)),
    openIconPicker: (target: Exclude<SpecsEditorIconPickerTarget, null>) =>
      dispatch(specsEditorActions.openIconPicker(target)),
    closeIconPicker: () => dispatch(specsEditorActions.closeIconPicker()),
    clearEditorState: () => {
      dispatch(specsEditorActions.resetRuntimeState());
      clearSession(storageKey);
      clearUploadState(uploadStorageKey);
      setSpecsModel(null);
      setIdentityOverrides({});
      productImport.setImportSelectedProduct("");
      productImport.setImportSelectedVersion("");
    },
  };
}

export type SpecsEditorApi = ReturnType<typeof useSpecsEditor>;

type UseSpecsEditorContextValueArgs = {
  editor: SpecsEditorApi;
  revisionHistory: SpecsRevisionHistoryValue;
  modals: SpecsEditorModalsApi;
};

export function useSpecsEditorContextValue(): SpecsEditorContextValue;
export function useSpecsEditorContextValue(
  args: UseSpecsEditorContextValueArgs
): SpecsEditorContextValue;
export function useSpecsEditorContextValue(
  args?: UseSpecsEditorContextValueArgs
) {
  const context = useContext(SpecsEditorContext);
  const effectiveLayerView = useSelector(selectEffectiveLayerView);
  const hoveredMaskUrl = useSelector(selectHoveredMaskUrl);
  const selectedPanelKind = useSelector(selectRightPanelKind);
  const editor = args?.editor ?? context?.editor ?? null;
  const modals = args?.modals ?? context?.modals ?? null;
  const revisionHistory =
    args?.revisionHistory ?? context?.revisionHistory ?? null;
  const selectedPositionId =
    editor?.session.nav.kind === "positions"
      ? (editor.session.nav.positionId ?? null)
      : null;

  const builtContextValue = useMemo(() => {
    if (!editor || !modals || !revisionHistory) {
      return null;
    }

    return {
      editor,
      ...editor,
      revisionHistory,
      modals,
      nav: editor.session.nav,
      effectiveLayerView,
      hoveredMaskUrl,
      selectedPanelKind,
      selectedPositionId,
      handleSelectDesign: editor.selectDesign,
    } satisfies SpecsEditorContextValue;
  }, [
    editor,
    effectiveLayerView,
    hoveredMaskUrl,
    revisionHistory,
    selectedPanelKind,
    selectedPositionId,
    modals,
  ]);

  if (args) {
    if (!builtContextValue) {
      throw new Error("Failed to build SpecsEditorContextValue");
    }

    return builtContextValue;
  }

  if (!context) {
    throw new Error(
      "useSpecsEditorContextValue must be used within a SpecsEditorContext provider"
    );
  }

  return context;
}
